import type { paths } from "../types/campaign-api.generated";
import * as api from "./client";

// ============================================================================
// Type Definitions
// ============================================================================

export type CampaignType = "survey_request" | "reminder";

export type CampaignStatus =
  | "synchronizing"
  | "draft"
  | "ready"
  | "scheduled"
  | "sending"
  | "sent"
  | "failed";

// ============================================================================
// Type Aliases from Generated OpenAPI Types
// ============================================================================

type ListCampaignsResponse =
  paths["/api/v1/mailing-groups/{id}/campaigns"]["get"]["responses"]["200"]["content"]["application/json"];

type CampaignResponse =
  paths["/api/v1/campaigns/{id}"]["get"]["responses"]["200"]["content"]["application/json"];

type RecipientsResponse =
  paths["/api/v1/campaigns/{id}/recipients"]["get"]["responses"]["200"]["content"]["application/json"];

type SegmentsListResponse =
  paths["/api/v1/campaigns/{id}/segments"]["get"]["responses"]["200"]["content"]["application/json"];

type SegmentPostResponse =
  paths["/api/v1/campaigns/{id}/segments"]["post"]["responses"]["201"]["content"]["application/json"];

type SegmentPatchResponse =
  paths["/api/v1/campaigns/{id}/segments/{segmentId}"]["patch"]["responses"]["200"]["content"]["application/json"];

type SegmentReadFields = {
  counts: SegmentCounts;
  latest_run: SegmentLatestRun | null;
  runs: SegmentRunRecord[];
  can_resume: boolean;
  needs_attention: boolean;
};

type SegmentGetResponse = { segment: Segment & SegmentReadFields; request_id?: string | null };

type ResumeSegmentResponse = {
  status: "re_enqueued";
  resumed_recipients: number;
  request_id?: string | null;
};

type TargetsListResponse =
  paths["/api/v1/campaigns/{id}/segments/{segmentId}/targets"]["get"]["responses"]["200"]["content"]["application/json"];

type ReplaceTargetsResponse =
  paths["/api/v1/campaigns/{id}/segments/{segmentId}/targets"]["put"]["responses"]["200"]["content"]["application/json"];

type ResyncResponse =
  paths["/api/v1/campaigns/{id}/resync"]["post"]["responses"]["200"]["content"]["application/json"];

type TestSendResponse =
  paths["/api/v1/campaigns/{id}/test-send"]["post"]["responses"]["200"]["content"]["application/json"];

type ResendRecipientsResponse =
  paths["/api/v1/campaigns/{id}/recipients/resend"]["post"]["responses"]["200"]["content"]["application/json"];

export type TestSendResult = { status: "sent" | "skipped"; messageId?: string };

// Summary returned by the resend endpoint: how many submit_failed recipients
// were reverted + re-enqueued, vs. skipped (in-flight segment / not failed).
export type ResendResult = { resent: number; skipped: number };

// Discriminated union: 'explicit' freezes exactly the given emails; 'filter'
// re-runs the recipient projection server-side, so large segments (100k+) never
// round-trip an email list over the wire (arch-discussion §14.1/§14.5 pt.1).
export type ReplaceTargetsBody =
  paths["/api/v1/campaigns/{id}/segments/{segmentId}/targets"]["put"]["requestBody"]["content"]["application/json"];

// Effective status of a deduped recipient in the projection (arch-discussion §14.7).
export type RecipientStatus = NonNullable<
  NonNullable<paths["/api/v1/campaigns/{id}/recipients"]["get"]["parameters"]["query"]>["status"]
>;

// The literal per-recipient send state (distinct from the derived RecipientStatus)
// — null until a campaign_recipient row exists for the email in this campaign.
export type RecipientSendStatus = NonNullable<
  NonNullable<
    paths["/api/v1/campaigns/{id}/recipients"]["get"]["parameters"]["query"]
  >["sendStatus"]
>;

// Survey-response filter state (orthogonal to status/sendStatus): none = not
// responded, partial = some links answered, full = all links answered.
export type RecipientResponseState = NonNullable<
  NonNullable<
    paths["/api/v1/campaigns/{id}/recipients"]["get"]["parameters"]["query"]
  >["responseState"]
>;

export type CampaignRecipient = RecipientsResponse["recipients"][number];

export type CampaignRecipientsResult = {
  recipients: CampaignRecipient[];
  total: number;
  limit: number;
  offset: number;
};

export type Segment = SegmentsListResponse["segments"][number];

// Nested live-send progress on a segment (KROK 5b). `null` until the send worker
// claims the segment; the generated spec types it as an inline object, so alias
// it here for the list/progress rendering. `status` ∈ running | completed | failed.
export type SegmentRun = NonNullable<Segment["run"]>;

// The five UI-facing segment lifecycle states. The spec types `status` as a
// plain `string`; the chip switch narrows onto this union.
export type SegmentStatus = "draft" | "scheduled" | "sending" | "sent" | "canceled";

export type SegmentCounts = {
  targets: number;
  emailed: number;
  not_emailed_yet: number;
  unconfirmed: number;
  skipped: number;
};

export type SegmentLatestRun = {
  id: string;
  status: string;
  error: string | null;
  started_at: string;
  finished_at: string | null;
};

export type SegmentRunRecord = {
  id: string;
  status: string;
  total: number;
  claimed: number;
  submitted: number;
  failed: number;
  suppressed: number;
  error: string | null;
  started_at: string;
  finished_at: string | null;
};

export type SegmentWithProgress = Segment & SegmentReadFields;

export type ResumeSegmentResult = { resumedRecipients: number };

export type SegmentTarget = TargetsListResponse["targets"][number];

export type RecipientFilters = {
  limit?: number;
  offset?: number;
  status?: RecipientStatus;
  firmRef?: string;
  regionId?: number;
  rankingEntityId?: string;
  // Real send-state filter (e.g. `submit_failed`), matched server-side so the
  // resend "select all matching" targets the whole set, not just the page.
  sendStatus?: RecipientSendStatus;
  // Free-text match on first name / surname / email / firm (case-insensitive,
  // resolved server-side). Empty string is treated as "no search".
  search?: string;
  // Survey-response filter (orthogonal to status/sendStatus): AND-combined
  // server-side with the other filters.
  responseState?: RecipientResponseState;
  // Activity of the campaign_send_segment that claimed the row: true = still
  // active (draft/scheduled/sending), false = no segment or a terminal one
  // (sent/canceled). Combined with `sendStatus: "submitting"` it isolates sends
  // stuck by a network timeout from ones that are merely in flight.
  sendActive?: boolean;
};

// Distinct firms/regions present in the projection, for filter dropdowns.
// Hand-written to avoid a full regen of campaign-api.generated.ts.
type RecipientFacetsResponse = {
  regions: {
    region_id: number | null;
    ranking_entity_id: string | null;
    region_name: string | null;
  }[];
  firms: { firm_ref: string; firm_name: string | null }[];
  request_id?: string;
};

export type RecipientRegionFilter = { regionId: number } | { rankingEntityId: string };

export type RecipientRegionFacet = RecipientRegionFilter & { regionName: string | null };

export type RecipientFacets = {
  regions: RecipientRegionFacet[];
  firms: { firmRef: string; firmName: string | null }[];
};

export type CreateSegmentInput = {
  label?: string;
  // ISO 8601 UTC instant; null = send now. Caller converts wall-clock → UTC.
  scheduledAt?: string | null;
  timezone?: string;
};

// The generated spec types `type`/`status` as plain `string`. Narrow them to the
// UI-facing unions (the chip/label switches rely on exhaustive coverage).
export type Campaign = Omit<CampaignResponse["campaign"], "type" | "status"> & {
  type: CampaignType;
  status: CampaignStatus;
};

// ============================================================================
// Service
// ============================================================================

export const campaignsService = {
  listForGroup: async (groupId: string | number): Promise<Campaign[]> => {
    const res = await api.get<ListCampaignsResponse>(`/v1/mailing-groups/${groupId}/campaigns`);
    return res.campaigns as Campaign[];
  },

  createForGroup: (groupId: string | number, body?: { templateId?: string }): Promise<Campaign> =>
    api
      .post<CampaignResponse>(`/v1/mailing-groups/${groupId}/campaigns`, body ?? {})
      .then(r => r.campaign as Campaign),

  getById: (id: string | number): Promise<Campaign> =>
    api.get<CampaignResponse>(`/v1/campaigns/${id}`).then(r => r.campaign as Campaign),

  update: (
    id: string | number,
    body: { name?: string; status?: CampaignStatus; templateId?: string },
  ): Promise<Campaign> =>
    api.patch<CampaignResponse>(`/v1/campaigns/${id}`, body).then(r => r.campaign as Campaign),

  remove: (id: string | number): Promise<void> => api.del<void>(`/v1/campaigns/${id}`),

  // Re-drive a survey invitation stuck in `synchronizing` (e.g. after a Redis
  // restart dropped its BullMQ job). Returns `re_enqueued` when a fresh sync job
  // was queued, or `in_progress` if one is already running.
  resyncCampaign: (id: string | number): Promise<ResyncResponse> =>
    api.post<ResyncResponse>(`/v1/campaigns/${id}/resync`),

  // Send the campaign's template to a single staff address with sample data so an
  // operator can preview the real email. `skipped` means SendGrid isn't configured.
  testSend: (id: string | number, email: string): Promise<TestSendResult> =>
    api
      .post<TestSendResponse>(`/v1/campaigns/${id}/test-send`, { email })
      .then(r => ({ status: r.status, messageId: r.messageId })),

  // --- Recipient projection (deduped by email, arch-discussion §14.7) ---
  getRecipients: (
    id: string | number,
    opts?: RecipientFilters,
  ): Promise<CampaignRecipientsResult> => {
    const qs = new URLSearchParams();
    if (opts?.limit !== undefined) qs.set("limit", String(opts.limit));
    if (opts?.offset !== undefined) qs.set("offset", String(opts.offset));
    if (opts?.status !== undefined) qs.set("status", opts.status);
    if (opts?.firmRef) qs.set("firmRef", opts.firmRef);
    if (opts?.regionId !== undefined) qs.set("regionId", String(opts.regionId));
    if (opts?.rankingEntityId !== undefined) qs.set("rankingEntityId", opts.rankingEntityId);
    if (opts?.sendStatus !== undefined) qs.set("sendStatus", opts.sendStatus);
    if (opts?.search) qs.set("search", opts.search);
    if (opts?.responseState !== undefined) qs.set("responseState", opts.responseState);
    // The API parses this as the literal "true"/"false" (a plain boolean coercion
    // would read "false" as true), so send the stringified boolean.
    if (opts?.sendActive !== undefined) qs.set("sendActive", String(opts.sendActive));
    const query = qs.toString();
    const suffix = query ? `?${query}` : "";
    return api
      .get<RecipientsResponse>(`/v1/campaigns/${id}/recipients${suffix}`)
      .then(({ recipients, total, limit, offset }) => ({ recipients, total, limit, offset }));
  },

  // Resend submit_failed recipients: reverts them pending → re-enqueues their
  // owning segment(s). The API skips recipients that aren't submit_failed or
  // whose segment is currently in-flight (surfaced via the `skipped` count).
  resendRecipients: (id: string | number, recipientIds: string[]): Promise<ResendResult> =>
    api
      .post<ResendRecipientsResponse>(`/v1/campaigns/${id}/recipients/resend`, { recipientIds })
      .then(r => ({ resent: r.resent, skipped: r.skipped })),

  // --- Distinct firms/regions in the projection (filter dropdowns) ---
  getRecipientFacets: (id: string | number): Promise<RecipientFacets> =>
    api.get<RecipientFacetsResponse>(`/v1/campaigns/${id}/recipients/facets`).then(res => ({
      regions: (res.regions ?? []).flatMap((r): RecipientRegionFacet[] => {
        if (r.ranking_entity_id) {
          return [{ rankingEntityId: r.ranking_entity_id, regionName: r.region_name }];
        }
        if (r.region_id !== null) return [{ regionId: r.region_id, regionName: r.region_name }];
        return [];
      }),
      firms: (res.firms ?? []).map(f => ({ firmRef: f.firm_ref, firmName: f.firm_name })),
    })),

  // --- Send segments (arch-discussion §14.1) ---
  listSegments: (id: string | number): Promise<SegmentWithProgress[]> =>
    api
      .get<SegmentsListResponse>(`/v1/campaigns/${id}/segments`)
      .then(r => r.segments as SegmentWithProgress[]),

  getSegment: (id: string | number, segmentId: string): Promise<SegmentWithProgress> =>
    api.get<SegmentGetResponse>(`/v1/campaigns/${id}/segments/${segmentId}`).then(r => r.segment),

  resumeSegment: (id: string | number, segmentId: string): Promise<ResumeSegmentResult> =>
    api
      .post<ResumeSegmentResponse>(`/v1/campaigns/${id}/segments/${segmentId}/resume`)
      .then(r => ({ resumedRecipients: r.resumed_recipients })),

  createSegment: (id: string | number, body: CreateSegmentInput): Promise<Segment> =>
    api.post<SegmentPostResponse>(`/v1/campaigns/${id}/segments`, body).then(r => r.segment),

  deleteSegment: (id: string | number, segmentId: string): Promise<void> =>
    api.del<void>(`/v1/campaigns/${id}/segments/${segmentId}`),

  // Schedule (or reschedule) a segment: PATCH status → 'scheduled'. `scheduledAt`
  // is an absolute UTC ISO instant, or null to send immediately. Enqueues the
  // real send job server-side (KROK 5b). Re-PATCHing 'scheduled' reschedules.
  scheduleSegment: (
    id: string | number,
    segmentId: string,
    body: { scheduledAt: string | null; timezone?: string },
  ): Promise<Segment> =>
    api
      .patch<SegmentPatchResponse>(`/v1/campaigns/${id}/segments/${segmentId}`, {
        status: "scheduled",
        ...body,
      })
      .then(r => r.segment),

  // Cancel a scheduled segment: PATCH status → 'canceled'. The API returns 409
  // if the segment is already sending/sent (surfaced to the operator as a toast).
  cancelSegment: (id: string | number, segmentId: string): Promise<Segment> =>
    api
      .patch<SegmentPatchResponse>(`/v1/campaigns/${id}/segments/${segmentId}`, {
        status: "canceled",
      })
      .then(r => r.segment),

  // --- Frozen segment targets (replace-set, draft-only) ---
  listTargets: (
    id: string | number,
    segmentId: string,
  ): Promise<{ targets: SegmentTarget[]; total: number }> =>
    api
      .get<TargetsListResponse>(`/v1/campaigns/${id}/segments/${segmentId}/targets`)
      .then(({ targets, total }) => ({ targets, total })),

  replaceTargets: (
    id: string | number,
    segmentId: string,
    body: ReplaceTargetsBody,
  ): Promise<number> =>
    api
      .put<ReplaceTargetsResponse>(`/v1/campaigns/${id}/segments/${segmentId}/targets`, body)
      .then(r => r.count),
};
